// src/autodesk_usage/pages/SalesIntelligencePage.tsx
// All comments are in English.

import React from "react";
import { useNavigate } from "react-router-dom";
import { User2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import SharedTable from "@/components/shared/SharedTable";
import Spinner from "@/components/shared/Spinner";
import ErrorState from "@/components/shared/ErrorState";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import RankingBarChart from "@/odoo/transactions_dashboard/components/RankingBarChart";
import { useSalesIntelligence } from "../hooks/useSalesIntelligence";
import type {
  SalesIntelligenceCompanyItem,
  SalesIntelligenceProductItem,
} from "../api/salesIntelligence";

type OpportunityCompanyRow = SalesIntelligenceCompanyItem & {
  product_active_seats: number;
  product_used_users: number;
  product_gap: number;
};

const formatNumber = (value: number | null | undefined) =>
  (value ?? 0).toLocaleString("en-US", {
    maximumFractionDigits: 0,
  });

const formatPercent = (value: number | null | undefined) =>
  `${(value ?? 0).toFixed(1)}%`;

const calculatePotential = (gap: number, owned: number) =>
  owned > 0 ? (gap / owned) * 100 : 0;

const calculateOpportunityPotential = (gap: number, owned: number) =>
  -calculatePotential(gap, owned);

const OPPORTUNITY_PRODUCT_MODELS = [
  "Docs",
  "AutoCAD",
  "AEC Collection",
  "Build",
  "Other Autodesk",
] as const;

const COMPANY_OPTIONS = [
  { value: "", label: "All" },
  { value: "1", label: "Forida" },
  { value: "2", label: "OpenBIM" },
] as const;

type OpportunityProductModel = (typeof OPPORTUNITY_PRODUCT_MODELS)[number];

const getOpportunityProductModel = (productName: string): OpportunityProductModel => {
  const value = productName.trim().toLowerCase();
  if (!value) return "Other Autodesk";
  if (
    value.includes("docs") ||
    value.includes("bim 360 docs") ||
    value.includes("construction cloud docs") ||
    value.includes("acc docs")
  ) {
    return "Docs";
  }
  if (value.includes("autocad")) {
    return "AutoCAD";
  }
  if (
    value.includes("aec collection") ||
    value.includes("revit") ||
    value.includes("civil 3d") ||
    value.includes("navisworks") ||
    value.includes("infraworks")
  ) {
    return "AEC Collection";
  }
  if (
    value.includes("build") ||
    value.includes("autodesk build") ||
    value.includes("construction cloud build")
  ) {
    return "Build";
  }
  return "Other Autodesk";
};

function SalespersonChip({ name }: { name?: string | null }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-[11px] font-medium text-blue-700 shadow-sm">
      <User2 className="h-3.5 w-3.5" />
      <span className="whitespace-nowrap">{name ?? "Unassigned"}</span>
    </span>
  );
}

function PotentialChip({ value }: { value: number }) {
  return (
    <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-600 shadow-sm shadow-amber-100/70">
      {formatPercent(value)} Potential
    </span>
  );
}

const monthShift = (date: Date, offset: number) => {
  const shifted = new Date(date.getFullYear(), date.getMonth() + offset, 1);
  return shifted;
};

const toMonthValue = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
};

function defaultPeriod() {
  const start = monthShift(new Date(), -1);
  return {
    startMonth: toMonthValue(start),
    endMonth: toMonthValue(start),
  };
}

function SummaryCard({
  label,
  value,
  accent = "default",
  helper,
  onClick,
}: {
  label: string;
  value: string;
  accent?: "default" | "primary";
  helper?: string;
  onClick?: () => void;
}) {
  return (
    <Card
      className={[
        "border-slate-200/70 shadow-sm transition-all",
        accent === "primary"
          ? "bg-gradient-to-br from-blue-600 to-blue-500 text-white shadow-blue-200/50"
          : "bg-white/90",
        onClick ? "cursor-pointer hover:-translate-y-0.5 hover:shadow-md" : "",
      ].join(" ")}
      onClick={onClick}
    >
      <CardContent className="p-5">
        <div className={accent === "primary" ? "text-blue-100" : "text-slate-500"}>
          {label}
        </div>
        <div className="mt-2 text-3xl font-semibold tracking-tight">{value}</div>
        {helper ? (
          <div className={["mt-1 text-xs", accent === "primary" ? "text-blue-100/80" : "text-slate-500"].join(" ")}>
            {helper}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function CompanyGapDialog({
  companies,
  totalCount,
  onRowClick,
  children,
}: {
  companies: SalesIntelligenceCompanyItem[];
  totalCount: number;
  onRowClick?: (company: SalesIntelligenceCompanyItem) => void;
  children: React.ReactNode;
}) {
  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="w-[min(96vw,1080px)] max-w-none">
        <DialogHeader>
          <DialogTitle>Companies by gap</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="text-sm text-muted-foreground">
            {totalCount.toLocaleString("en-US")} companies are listed for the selected period.
          </div>
          <div className="max-h-[70vh] overflow-auto rounded-xl border">
            <SharedTable
              rows={companies}
              cols={[
                {
                  key: "company_name",
                  header: "Company",
                  render: (row: SalesIntelligenceCompanyItem) => (
                    <div>
                      <div className="font-medium text-slate-900">{row.company_name}</div>
                      <div className="text-xs text-muted-foreground">
                        {row.products.length} products
                      </div>
                    </div>
                  ),
                },
                {
                  key: "total_gap",
                  header: "Total gap",
                  sortType: "number",
                  render: (row: SalesIntelligenceCompanyItem) => (
                    <span
                      className={[
                        "font-semibold",
                        row.total_gap >= 0 ? "text-rose-600" : "text-emerald-600",
                      ].join(" ")}
                    >
                      {formatNumber(row.total_gap)}
                    </span>
                  ),
                },
                {
                  key: "total_active_seats",
                  header: "Active seats",
                  sortType: "number",
                  render: (row: SalesIntelligenceCompanyItem) => formatNumber(row.total_active_seats),
                },
                {
                  key: "total_used_users",
                  header: "Used users",
                  sortType: "number",
                  render: (row: SalesIntelligenceCompanyItem) => formatNumber(row.total_used_users),
                },
                {
                  key: "products",
                  header: "Product breakdown",
                  sortable: false,
                  render: (row: SalesIntelligenceCompanyItem) => (
                    <div className="flex flex-wrap gap-1">
                      {row.products.map((product) => (
                        <Badge key={product.product} variant="secondary" className="rounded-full">
                          {product.product}: {formatNumber(product.total_gap)}
                        </Badge>
                      ))}
                    </div>
                  ),
                },
              ]} 
              onRowClick={onRowClick}
              emptyText="No companies found in the selected period."
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ProductOpportunityDialog({
  title,
  companies,
  totalCount,
  onRowClick,
  children,
}: {
  title: string;
  companies: OpportunityCompanyRow[];
  totalCount: number;
  onRowClick?: (company: OpportunityCompanyRow) => void;
  children: React.ReactNode;
}) {
  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="w-[min(96vw,1080px)] max-w-none">
        <DialogHeader>
          <DialogTitle>{title} opportunity breakdown</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="text-sm text-muted-foreground">
            {totalCount.toLocaleString("en-US")} companies are listed for this product model in the selected period.
          </div>
          <div className="max-h-[70vh] overflow-auto rounded-xl border">
            <SharedTable
              rows={companies}
              cols={[
                {
                  key: "company_name",
                  header: "Site entity",
                  render: (row: OpportunityCompanyRow) => (
                    <div>
                      <div className="font-medium text-slate-900">{row.company_name}</div>
                      <div className="text-xs text-muted-foreground">
                        {row.product_active_seats.toLocaleString("en-US")} Forida, {row.product_used_users.toLocaleString("en-US")} Active License
                      </div>
                    </div>
                  ),
                },
                {
                  key: "salesperson_name",
                  header: "Sales person",
                  sortType: "string",
                  render: (row: OpportunityCompanyRow) => (
                    <SalespersonChip name={row.salesperson_name} />
                  ),
                },
                {
                  key: "product_active_seats",
                  header: "Forida",
                  sortType: "number",
                  render: (row: OpportunityCompanyRow) => formatNumber(row.product_active_seats),
                },
                {
                  key: "product_used_users",
                  header: "Active License",
                  sortType: "number",
                  render: (row: OpportunityCompanyRow) => formatNumber(row.product_used_users),
                },
                {
                  key: "product_gap",
                  header: "Gap",
                  sortType: "number",
                  render: (row: OpportunityCompanyRow) => (
                    <span
                      className={[
                        "font-semibold",
                        row.product_gap >= 0 ? "text-rose-600" : "text-emerald-600",
                      ].join(" ")}
                    >
                      {formatNumber(row.product_gap)}
                    </span>
                  ),
                },
                {
                  key: "potential",
                  header: "Potential",
                  sortType: "number",
                  getValue: (row: OpportunityCompanyRow) =>
                    calculateOpportunityPotential(
                      row.product_gap,
                      row.product_used_users
                    ),
                  render: (row: OpportunityCompanyRow) => (
                    <PotentialChip
                      value={calculateOpportunityPotential(
                        row.product_gap,
                        row.product_used_users
                      )}
                    />
                  ),
                },
              ]} 
              onRowClick={onRowClick}
              rowTooltipContent={renderProductOpportunityTooltip}
              emptyText="No companies found for this product."
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function renderProductOpportunityTooltip(row: SalesIntelligenceCompanyItem) {
  return (
        <div className="space-y-2">
      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
        Product breakdown
      </div>
      <div className="space-y-1.5">
        {row.products.map((product) => (
          <div
            key={`${row.company_name}-${product.product}`}
            className="flex items-center justify-between gap-3 text-xs"
          >
            <span className="font-medium text-slate-700">
              {product.product}
            </span>
            <span className="tabular-nums font-semibold text-rose-600">
              {formatNumber(product.total_gap)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProductOpportunityCard({
  title,
  companies,
  onCompanyClick,
}: {
  title: string;
  companies: OpportunityCompanyRow[];
  onCompanyClick?: (companyName: string) => void;
}) {
  const topCompanies = companies.slice(0, 3);
  const totalGap = companies.reduce((sum, company) => sum + company.product_gap, 0);
  const totalCount = companies.length;
  const showAll = totalCount > topCompanies.length;

  return (
    <Card className="rounded-3xl border-slate-200/70 bg-white/90 shadow-sm backdrop-blur">
      <CardHeader className="flex flex-row items-start justify-between gap-3 pb-3">
        <div>
          <CardTitle className="text-2xl font-semibold tracking-tight">
            {title}
          </CardTitle>
          <div
            className={[
              "mt-1 text-xs font-semibold uppercase tracking-[0.16em]",
              totalGap >= 0 ? "text-rose-500" : "text-emerald-500",
            ].join(" ")}
          >
            Total gap: {formatNumber(totalGap)}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {showAll ? (
            <ProductOpportunityDialog
              title={title}
              companies={companies}
              totalCount={totalCount}
              onRowClick={(company) => onCompanyClick?.(company.company_name)}
            >
              <Button variant="outline" size="sm" className="shrink-0">
                Show All ({totalCount})
              </Button>
            </ProductOpportunityDialog>
          ) : null}
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        <SharedTable
          rows={topCompanies}
          cols={[
            {
              key: "company_name",
              header: "Site entity",
              render: (row: OpportunityCompanyRow) => (
                <div>
                  <div className="font-semibold text-slate-900">
                    {row.company_name}
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.12em] text-slate-400">
                    {row.products.length} products
                  </div>
                </div>
              ),
            },
            {
              key: "salesperson_name",
              header: "Sales person",
              sortType: "string",
              render: (row: OpportunityCompanyRow) => (
                <SalespersonChip name={row.salesperson_name} />
              ),
            },
            {
              key: "product_active_seats",
              header: "Forida",
              sortType: "number",
              render: (row: OpportunityCompanyRow) =>
                formatNumber(row.product_active_seats),
            },
            {
              key: "product_used_users",
              header: "Active License",
              sortType: "number",
              render: (row: OpportunityCompanyRow) =>
                formatNumber(row.product_used_users),
            },
            {
              key: "product_gap",
              header: "Gap",
              sortType: "number",
              render: (row: OpportunityCompanyRow) => (
                <span
                  className={[
                    "font-semibold",
                    row.product_gap >= 0 ? "text-rose-600" : "text-emerald-600",
                  ].join(" ")}
                >
                  {formatNumber(row.product_gap)}
                </span>
              ),
            },
            {
              key: "potential",
              header: "Potential",
              sortType: "number",
              getValue: (row: OpportunityCompanyRow) =>
                calculatePotential(row.product_gap, row.product_used_users),
              render: (row: OpportunityCompanyRow) => (
                <PotentialChip
                  value={calculatePotential(row.product_gap, row.product_used_users)}
                />
              ),
            },
          ]}
          defaultSort={{ key: "product_gap", dir: "asc" }}
          onRowClick={
            onCompanyClick
              ? (company) => onCompanyClick(company.company_name)
              : undefined
          }
          rowTooltipContent={renderProductOpportunityTooltip}
          emptyText="No companies found for this product."
        />
      </CardContent>
    </Card>
  );
}

export default function SalesIntelligencePage() {
  const navigate = useNavigate();
  const period = React.useMemo(() => defaultPeriod(), []);
  const [startMonth, setStartMonth] = React.useState(period.startMonth);
  const [endMonth, setEndMonth] = React.useState(period.endMonth);
  const [companyId, setCompanyId] = React.useState<string>("1");
  const [salespersonName, setSalespersonName] = React.useState<string>("__all__");
  const [opportunityProductModel, setOpportunityProductModel] = React.useState<string>("__all__");

  const selectedCompanyId = companyId === "" ? undefined : Number(companyId);
  const selectedSalesperson =
    salespersonName === "__all__" ? undefined : salespersonName;

  const query = useSalesIntelligence(
    startMonth,
    endMonth,
    selectedCompanyId,
    selectedSalesperson
  );
  const data = query.data;

  const productGroups = data?.product_groups ?? [];
  const allProducts = data?.all_products ?? [];
  const allCompanies = data?.all_companies ?? [];
  const topCompanies = allCompanies.slice(0, 5);
  const opportunityProducts = React.useMemo<
    Array<SalesIntelligenceProductItem & { companies: OpportunityCompanyRow[] }>
  >(
    () =>
      allProducts
        .map((product) => ({
          ...product,
          companies: allCompanies
            .map((company) => {
              const productRow = company.products.find(
                (companyProduct) => companyProduct.product === product.product
              );
              if (!productRow) return null;
              return {
                ...company,
                product_active_seats: productRow.total_active_seats,
                product_used_users: productRow.total_used_users,
                product_gap: productRow.total_gap,
              };
            })
            .filter(
              (
                company
              ): company is OpportunityCompanyRow => company !== null
            )
            .sort(
              (a, b) =>
                a.product_gap - b.product_gap ||
                a.company_name.localeCompare(b.company_name)
            ),
        }))
        .filter((product) => product.companies.length > 0)
        .sort((a, b) => a.total_gap - b.total_gap),
    [allCompanies, allProducts]
  );
  const filteredOpportunityProducts = React.useMemo(
    () =>
      opportunityProducts.filter((product) => {
        if (opportunityProductModel === "__all__") return true;
        return getOpportunityProductModel(product.product) === opportunityProductModel;
      }),
    [opportunityProductModel, opportunityProducts]
  );

  const groupRows = React.useMemo(
    () =>
      productGroups.map((group) => ({
        id: group.product_group,
        label: group.product_group,
        value: group.total_gap,
        secondaryLabel: "Active seats",
        secondaryValue: group.total_active_seats,
      }))
        .sort((a, b) => a.value - b.value),
    [productGroups]
  );

  const totalGapValue = data?.total_gap ?? 0;
  const salespersonOptions = data?.salespersons ?? [];

  const handleCompanyOpen = React.useCallback(
    (companyName: string) => {
      if (!companyName) return;
      navigate(`/autodesk-usage/corporate?company_name=${encodeURIComponent(companyName)}`);
    },
    [navigate]
  );

  return (
    <div className="relative space-y-6">
      <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-72 bg-[radial-gradient(circle_at_top_left,_rgba(37,99,235,0.16),_transparent_36%),radial-gradient(circle_at_top_right,_rgba(14,165,233,0.10),_transparent_28%),linear-gradient(to_bottom,_rgba(248,250,252,0.96),_transparent)]" />

      <PageHeader
        title="Sales Intelligence (Autodesk)"
        description="Autodesk gap analysis by product group and company over a selected period, ordered from most negative gap upward."
      />

      <SectionCard>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
              Selected Period
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              Gap is calculated as active seats minus used users across the chosen month window.
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <label className="flex flex-col gap-1 text-xs font-medium text-slate-500">
              Company ID (optional)
              <select
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
                className="h-10 w-[220px] rounded-lg border border-slate-200 bg-white px-3 text-sm shadow-sm outline-none ring-0 transition focus:border-blue-500"
              >
                {COMPANY_OPTIONS.map((option) => (
                  <option key={option.value || "all"} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs font-medium text-slate-500">
              Sales person
              <Select value={salespersonName} onValueChange={setSalespersonName}>
                <SelectTrigger className="h-10 w-[220px] rounded-lg border border-slate-200 bg-white px-3 text-sm shadow-sm outline-none ring-0 transition focus:border-blue-500">
                  <SelectValue placeholder="All sales people" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All sales people</SelectItem>
                  {salespersonOptions.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
            <label className="flex flex-col gap-1 text-xs font-medium text-slate-500">
              Start month
              <input
                type="month"
                value={startMonth}
                onChange={(e) => setStartMonth(e.target.value)}
                className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm shadow-sm outline-none ring-0 transition focus:border-blue-500"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs font-medium text-slate-500">
              End month
              <input
                type="month"
                value={endMonth}
                onChange={(e) => setEndMonth(e.target.value)}
                className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm shadow-sm outline-none ring-0 transition focus:border-blue-500"
              />
            </label>
          </div>
        </div>
      </SectionCard>

      {query.isLoading ? (
        <div className="rounded-2xl border bg-white p-6 shadow-sm">
          <Spinner label="Loading sales intelligence..." />
        </div>
      ) : query.isError ? (
        <ErrorState
          text={
            (query.error as any)?.message ||
            "Failed to load sales intelligence."
          }
        />
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <SummaryCard
              label="Total gap (qty)"
              value={formatNumber(totalGapValue)}
              helper={`${data?.start_month ?? startMonth} to ${data?.end_month ?? endMonth}`}
            />
            <SummaryCard
              label="Active seats"
              value={formatNumber(data?.total_active_seats)}
              helper="Summed across the selected period"
            />
            <SummaryCard
              label="Used users"
              value={formatNumber(data?.total_used_users)}
              helper="Summed across the selected period"
            />
            <CompanyGapDialog
              companies={allCompanies}
              totalCount={allCompanies.length}
              onRowClick={(company) => handleCompanyOpen(company.company_name)}
            >
              <SummaryCard
                label="Companies"
                value={formatNumber(allCompanies.length)}
                accent="primary"
                helper={`Company scope: ${
                  COMPANY_OPTIONS.find((opt) => opt.value === companyId)?.label ?? "All"
                }`}
              />
            </CompanyGapDialog>
          </div>

          <section className="grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
            <Card className="rounded-3xl border-slate-200/70 bg-white/90 shadow-sm backdrop-blur">
              <CardHeader className="pb-3">
                <CardTitle className="text-xl font-semibold tracking-tight">
                  Gap by Product Group
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Grouped with the same Autodesk product normalization used by the dashboard and ordered from most negative gap upward.
                </p>
              </CardHeader>
              <CardContent>
                <RankingBarChart
                  title="Gap by product group"
                  showHeader={false}
                  rows={groupRows}
                  valueLabel="Gap"
                  valueFormatter={formatNumber}
                  secondaryValueFormatter={formatNumber}
                  barColor="#2563eb"
                  isLoading={false}
                  error={null}
                  emptyMessage="No group gap data for this period."
                />
              </CardContent>
            </Card>

            <Card className="rounded-3xl border-slate-200/70 bg-white/90 shadow-sm backdrop-blur">
              <CardHeader className="flex flex-row items-start justify-between gap-3 pb-3">
                <div>
                  <CardTitle className="text-xl font-semibold tracking-tight">
                    Companies by Gap
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Ranked from most negative to most positive total gap.
                  </p>
                </div>
                <CompanyGapDialog
                  companies={allCompanies}
                  totalCount={allCompanies.length}
                  onRowClick={(company) => handleCompanyOpen(company.company_name)}
                >
                  <Button variant="outline" size="sm" className="shrink-0">
                    Show All
                  </Button>
                </CompanyGapDialog>
              </CardHeader>
      <CardContent className="space-y-3">
        <TooltipProvider delayDuration={150}>
          {topCompanies.length === 0 ? (
                  <div className="rounded-xl border border-dashed bg-muted/30 px-4 py-8 text-center text-sm text-muted-foreground">
                    No companies found in the selected period.
                  </div>
                ) : (
                  topCompanies.map((company, index) => (
                    <Tooltip key={company.company_name}>
                      <TooltipTrigger asChild>
                        <div
                          className="flex cursor-pointer items-center justify-between gap-3 rounded-2xl border border-slate-200/70 bg-slate-50/70 px-4 py-3 transition-colors hover:bg-slate-50"
                          onClick={() => handleCompanyOpen(company.company_name)}
                        >
                          <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-xs font-semibold text-slate-600 shadow-sm">
                              {index + 1}
                            </div>
                            <div>
                              <div className="font-semibold text-slate-900">
                                {company.company_name}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {company.product_groups.length} product groups
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                              Total gap
                            </div>
                            <div
                              className={[
                                "text-lg font-semibold",
                                company.total_gap >= 0 ? "text-rose-600" : "text-emerald-600",
                              ].join(" ")}
                            >
                              {formatNumber(company.total_gap)}
                            </div>
                          </div>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-sm rounded-lg border border-slate-200 bg-white p-3 text-slate-900 shadow-lg">
                        <div className="space-y-2">
                          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                            Product group breakdown
                          </div>
                          <div className="space-y-1.5">
                            {company.product_groups.map((group) => (
                              <div
                                key={`${company.company_name}-${group.product_group}`}
                                className="flex items-center justify-between gap-3 text-xs"
                              >
                                <span className="font-medium text-slate-700">
                                  {group.product_group}
                                </span>
                                <span
                                  className={[
                                    "tabular-nums font-semibold",
                                    group.total_gap >= 0 ? "text-rose-600" : "text-emerald-600",
                                  ].join(" ")}
                                >
                                  {formatNumber(group.total_gap)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  ))
                )}
        </TooltipProvider>
              </CardContent>
            </Card>
          </section>

          <section className="space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="text-2xl font-semibold tracking-tight">
                  Product Opportunity Breakdown
                </h2>
                <p className="text-sm text-muted-foreground uppercase tracking-[0.14em]">
                  Focusing on high-growth potential accounts
                </p>
              </div>

              <label className="flex flex-col gap-1 text-xs font-medium text-slate-500 sm:min-w-[220px] sm:items-end">
                Product Model
                <Select value={opportunityProductModel} onValueChange={setOpportunityProductModel}>
                  <SelectTrigger className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm shadow-sm outline-none ring-0 transition focus:border-blue-500 sm:w-[220px]">
                    <SelectValue placeholder="All product models" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All product models</SelectItem>
                    {OPPORTUNITY_PRODUCT_MODELS.map((productModel) => (
                      <SelectItem key={productModel} value={productModel}>
                        {productModel}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>
            </div>

            <div className="space-y-4">
              {filteredOpportunityProducts.length === 0 ? (
                <Card className="rounded-3xl border-slate-200/70 bg-white/90 shadow-sm backdrop-blur">
                  <CardContent className="px-5 py-8 text-center text-sm text-muted-foreground">
                    No product opportunity data for the selected period.
                  </CardContent>
                </Card>
              ) : (
                filteredOpportunityProducts.map((product) => (
                  <ProductOpportunityCard
                    key={product.product}
                    title={product.product}
                    companies={product.companies}
                    onCompanyClick={handleCompanyOpen}
                  />
                ))
              )}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
